const isProduction = process.env.NODE_ENV === 'production';

export enum ConsoleID {
  AdminControlPanel = 'AdminControlPanel',
  AdminStatsAPI = 'AdminStatsAPI',
  App = 'App',
  AppUISlice = 'AppUISlice',
  BubbleSelector = 'BubbleSelector',
  CategoryRankings = 'CategoryRankings',
  GameDetails = 'GameDetails',
  GameDetailsHeading = 'GameDetailsHeading',
  GameScheduleSlice = 'GameScheduleSlice',
  IndexDb = 'IndexDb',
  IndexedDBInterface = 'IndexedDBInterface',
  Landing = 'Landing',
  LeagueSlice = 'LeagueSlice',
  Popup = 'Popup',
  RankingSlice = 'RankingSlice',
  SingleGameScorebox = 'SingleGameScorebox',
  Standings = 'Standings',
  StarterScreen = 'StarterScreen',
  StatsManager = 'StatsManager',
  SyncManagerAPI = 'SyncManagerAPI',
  Team = 'Team',
  TeamRanking = 'TeamRanking',
  TeamStat = 'TeamStat',
  UseAdminActions = 'UseAdminActions',
  UseAdminStatsCopyActions = 'UseAdminStatsCopyActions',
  UseAdminStatsGameIDsSelector = 'UseAdminStatsGameIDsSelector',
  UseAdminStatsLoadGameBoxScoreActions = 'UseAdminStatsLoadGameBoxScoreActions',
  UseAdminStatsSetWeeklyGameIDs = 'UseAdminStatsSetWeeklyGameIDs',
  UseAllTeamStatsInfo = 'UseAllTeamStatsInfo',
  UseAuth = 'UseAuth',
  UseBackgroundLoader = 'UseBackgroundLoader',
  UseCategoryRankings = 'UseCategoryRankings',
  UseDataLoaderForCategoryRankings = 'UseDataLoaderForCategoryRankings',
  UseDataLoaderForGameDetails = 'UseDataLoaderForGameDetails',
  UseDataLoaderForHeadToHead = 'UseDataLoaderForHeadToHead',
  UseDataLoaderForRanks = 'UseDataLoaderForRanks',
  UseDataLoaderForStandings = 'UseDataLoaderForStandings',
  UseDataLoaderForTeams = 'UseDataLoaderForTeams',
  UseDataLoaderForTeamStats = 'UseDataLoaderForTeamStats',
  UseGameContext = 'UseGameContext',
  UseGameStatsManager = 'UseGameStatsManager',
  UseOppositeTeamsStrengthObserver = 'UseOppositeTeamsStrengthObserver',
  UsePredictionDataLoader = 'UsePredictionDataLoader',
  UseSeasonStandings = 'UseSeasonStandings',
  UseSelectedScene = 'UseSelectedScene',
  UseStatAppInitializer = 'UseStatAppInitializer',
  UseStatAppMonitor = 'UseStatAppMonitor',
  UseStatManager = 'UseStatManager',
  UseSyncManager = 'UseSyncManager',
  ViewTeam = 'ViewTeam',
  ViewTeamIndex = 'ViewTeamIndex',
  ViewTeamLoader = 'ViewTeamLoader',
  WebWorkerIDBLoader = 'WebWorkerIDBLoader',
  WeeklyGames = 'WeeklyGames',
  WeeklyGamesLoader = 'WeeklyGamesLoader',
}

const ConsolePrintState: { [key in ConsoleID]: boolean } = {
  [ConsoleID.AdminControlPanel]: false,
  [ConsoleID.AdminStatsAPI]: true,
  [ConsoleID.App]: true,
  [ConsoleID.AppUISlice]: true,
  [ConsoleID.BubbleSelector]: false,
  [ConsoleID.CategoryRankings]: false,
  [ConsoleID.GameDetails]: true,
  [ConsoleID.GameDetailsHeading]: true,
  [ConsoleID.GameScheduleSlice]: false,
  [ConsoleID.IndexDb]: false,
  [ConsoleID.IndexedDBInterface]: true,
  [ConsoleID.Landing]: false,
  [ConsoleID.LeagueSlice]: false,
  [ConsoleID.Popup]: false,
  [ConsoleID.RankingSlice]: true,
  [ConsoleID.SingleGameScorebox]: true,
  [ConsoleID.Standings]: false,
  [ConsoleID.StarterScreen]: true,
  [ConsoleID.StatsManager]: true,
  [ConsoleID.SyncManagerAPI]: true,
  [ConsoleID.Team]: false,
  [ConsoleID.TeamRanking]: true,
  [ConsoleID.TeamStat]: false,
  [ConsoleID.UseAdminActions]: true,
  [ConsoleID.UseAdminStatsCopyActions]: false,
  [ConsoleID.UseAdminStatsGameIDsSelector]: false,
  [ConsoleID.UseAdminStatsLoadGameBoxScoreActions]: false,
  [ConsoleID.UseAdminStatsSetWeeklyGameIDs]: false,
  [ConsoleID.UseAllTeamStatsInfo]: false,
  [ConsoleID.UseAuth]: true,
  [ConsoleID.UseBackgroundLoader]: true,
  [ConsoleID.UseCategoryRankings]: true,
  [ConsoleID.UseDataLoaderForCategoryRankings]: true,
  [ConsoleID.UseDataLoaderForGameDetails]: false,
  [ConsoleID.UseDataLoaderForHeadToHead]: false,
  [ConsoleID.UseDataLoaderForRanks]: false,
  [ConsoleID.UseDataLoaderForStandings]: true,
  [ConsoleID.UseDataLoaderForTeams]: false,
  [ConsoleID.UseDataLoaderForTeamStats]: true,
  [ConsoleID.UseGameContext]: true,
  [ConsoleID.UseGameStatsManager]: true,
  [ConsoleID.UseOppositeTeamsStrengthObserver]: true,
  [ConsoleID.UsePredictionDataLoader]: true,
  [ConsoleID.UseSeasonStandings]: true,
  [ConsoleID.UseSelectedScene]: true,
  [ConsoleID.UseStatAppInitializer]: true,
  [ConsoleID.UseStatAppMonitor]: true,
  [ConsoleID.UseStatManager]: true,
  [ConsoleID.UseSyncManager]: true,
  [ConsoleID.ViewTeam]: false,
  [ConsoleID.ViewTeamIndex]: false,
  [ConsoleID.ViewTeamLoader]: false,
  [ConsoleID.WebWorkerIDBLoader]: true,
  [ConsoleID.WeeklyGames]: true,
  [ConsoleID.WeeklyGamesLoader]: true,
};

export enum ConsoleGroupID {
  All = 'all',
  Admin = 'admin',
  Hooks = 'hooks',
  Slices = 'slices',
  Scenes = 'scenes',
  Components = 'components',
  Lib = 'lib',
  App = 'app',
}

export const ConsoleGroups: { [key in ConsoleGroupID]: ConsoleID[] } = {
  [ConsoleGroupID.All]: Object.values(ConsoleID),
  [ConsoleGroupID.Admin]: [
    ConsoleID.AdminControlPanel,
    ConsoleID.UseAdminActions,
    ConsoleID.UseAdminStatsCopyActions,
    ConsoleID.UseAdminStatsGameIDsSelector,
    ConsoleID.UseAdminStatsLoadGameBoxScoreActions,
    ConsoleID.UseAdminStatsSetWeeklyGameIDs,
  ],
  [ConsoleGroupID.Hooks]: [
    ConsoleID.UseAuth,
    ConsoleID.UseBackgroundLoader,
    ConsoleID.UseDataLoaderForGameDetails,
    ConsoleID.UseDataLoaderForRanks,
    ConsoleID.UseDataLoaderForTeams,
    ConsoleID.UseGameStatsManager,
    ConsoleID.UseSeasonStandings,
    ConsoleID.UseSelectedScene,
    ConsoleID.UseStatAppInitializer,
    ConsoleID.UseStatAppMonitor,
    ConsoleID.UseStatManager,
    ConsoleID.UseSyncManager,
  ],
  [ConsoleGroupID.Slices]: [
    ConsoleID.AppUISlice,
    ConsoleID.GameScheduleSlice,
    ConsoleID.LeagueSlice,
    ConsoleID.RankingSlice,
  ],
  [ConsoleGroupID.Scenes]: [ConsoleID.Landing, ConsoleID.StatsManager, ConsoleID.Standings, ConsoleID.WeeklyGames],
  [ConsoleGroupID.Components]: [
    ConsoleID.GameDetails,
    ConsoleID.GameDetailsHeading,
    ConsoleID.Popup,
    ConsoleID.StarterScreen,
    ConsoleID.Team,
    ConsoleID.TeamStat,
    ConsoleID.ViewTeam,
    ConsoleID.ViewTeamIndex,
    ConsoleID.ViewTeamLoader,
  ],
  [ConsoleGroupID.Lib]: [
    ConsoleID.IndexDb,
    ConsoleID.IndexedDBInterface,
    ConsoleID.SyncManagerAPI,
    ConsoleID.WebWorkerIDBLoader,
  ],
  [ConsoleGroupID.App]: [ConsoleID.App],
};

export const getConsoleGroup = (id: ConsoleID): ConsoleGroupID | undefined => {
  return Object.keys(ConsoleGroups).find((group) => ConsoleGroups[group as ConsoleGroupID].includes(id)) as
    | ConsoleGroupID
    | undefined;
};

const ConsoleGroupPrintState: { [key in ConsoleGroupID]: boolean } = {
  [ConsoleGroupID.All]: false,
  [ConsoleGroupID.Admin]: false,
  [ConsoleGroupID.Hooks]: true,
  [ConsoleGroupID.Slices]: true,
  [ConsoleGroupID.Scenes]: true,
  [ConsoleGroupID.Components]: false,
  [ConsoleGroupID.Lib]: true,
  [ConsoleGroupID.App]: true,
};

export const getConsoleGroupState = (id: ConsoleGroupID): boolean => {
  if (id in ConsoleGroupPrintState && !isProduction) {
    return ConsoleGroupPrintState[id];
  }

  return false;
};

export const getConsoleState = (id: ConsoleID): boolean => {
  if (id in ConsolePrintState && !isProduction) {
    return ConsolePrintState[id];
  }

  return false;
};

export const setConsoleState = (id: ConsoleID, state: boolean) => {
  if (id in ConsolePrintState) {
    ConsolePrintState[id] = state;
  }
};

export const setConsoleGroupState = (id: ConsoleGroupID, state: boolean) => {
  if (id in ConsoleGroupPrintState) {
    ConsoleGroupPrintState[id] = state;
    const consoleIDs = ConsoleGroups[id];
    consoleIDs.forEach((consoleID) => {
      setConsoleState(consoleID, state);
    });
  }
};

// --- IGNORE ---
// Example usage:
// const CONSOLE_ON = getConsoleState(ConsoleID.StatsManager);
// if (CONSOLE_ON) console.log('This is a log message from StatsManager');
// --- IGNORE ---
